import { useEffect, useSyncExternalStore } from "react";

const KEY_API = "ci.api_key";
const KEY_PROJECT = "ci.project_id";
const KEY_BOOTSTRAP = "ci.bootstrap_token";
const KEY_COGNITO_TOKEN = "ci.cognito_id_token";
const KEY_IS_ADMIN = "ci.is_admin";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) || "https://code-insights-dev-api.agentic-ace.io";

const listeners = new Set<() => void>();
function emit() { listeners.forEach((l) => l()); }
function subscribe(cb: () => void) { listeners.add(cb); return () => { listeners.delete(cb); }; }

export function getApiKey(): string | null { return localStorage.getItem(KEY_API); }
export function getProjectId(): string | null { return localStorage.getItem(KEY_PROJECT); }
export function getBootstrapToken(): string | null { return localStorage.getItem(KEY_BOOTSTRAP); }
export function getCognitoToken(): string | null { return localStorage.getItem(KEY_COGNITO_TOKEN); }
export function getIsAdmin(): boolean { return localStorage.getItem(KEY_IS_ADMIN) === "1"; }

// Server is the source of truth for admin status (GET /api/v1/admin/whoami checks
// the caller's Cognito group / bootstrap token) — this just caches the result so
// every useAuth() call site doesn't refetch. Guarded so concurrent mounts share
// one in-flight request per token.
let adminCheckedForToken: string | null = "unset";
let adminCheckInFlight: Promise<void> | null = null;

function refreshIsAdmin(): Promise<void> {
  const token = getApiKey() || getCognitoToken();
  if (token === adminCheckedForToken) return adminCheckInFlight ?? Promise.resolve();
  if (adminCheckInFlight) return adminCheckInFlight;

  adminCheckedForToken = token;
  adminCheckInFlight = (async () => {
    if (!token) {
      localStorage.removeItem(KEY_IS_ADMIN);
      emit();
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/api/v1/admin/whoami`, { headers: { Authorization: `Bearer ${token}` } });
      const isAdmin = r.ok && (await r.json()).is_admin === true;
      localStorage.setItem(KEY_IS_ADMIN, isAdmin ? "1" : "0");
    } catch {
      localStorage.setItem(KEY_IS_ADMIN, "0");
    }
    emit();
  })().finally(() => {
    adminCheckInFlight = null;
  });
  return adminCheckInFlight;
}

export function signIn(projectId: string, apiKey: string) {
  localStorage.setItem(KEY_API, apiKey);
  localStorage.setItem(KEY_PROJECT, projectId);
  emit();
}

// Cognito Hosted UI login resolves an org, not a specific project — no
// project id is stored here; repo-scoped pages read their own project id
// from the route instead (see api/client.ts's projectScope override).
export function signInWithCognito(idToken: string) {
  localStorage.setItem(KEY_COGNITO_TOKEN, idToken);
  emit();
}

export function signOut() {
  localStorage.removeItem(KEY_API);
  localStorage.removeItem(KEY_PROJECT);
  localStorage.removeItem(KEY_COGNITO_TOKEN);
  localStorage.removeItem(KEY_IS_ADMIN);
  adminCheckedForToken = "unset";
  emit();
}

export function saveBootstrapToken(token: string) {
  localStorage.setItem(KEY_BOOTSTRAP, token);
  emit();
}

export function clearBootstrapToken() {
  localStorage.removeItem(KEY_BOOTSTRAP);
  emit();
}

export function useAuth() {
  const snapshot = useSyncExternalStore(
    subscribe,
    () =>
      `${getApiKey() || ""}|${getProjectId() || ""}|${getBootstrapToken() || ""}|${getCognitoToken() || ""}|${getIsAdmin() ? "1" : "0"}`,
  );

  const authToken = getApiKey() || getCognitoToken();
  useEffect(() => {
    if (authToken) refreshIsAdmin();
  }, [authToken]);

  return {
    authed: !!getProjectId() || !!getCognitoToken(),
    apiKey: getApiKey(),
    projectId: getProjectId(),
    bootstrapToken: getBootstrapToken(),
    hasBootstrapToken: !!getBootstrapToken(),
    cognitoToken: getCognitoToken(),
    isAdmin: getIsAdmin(),
    signIn,
    signInWithCognito,
    logout: signOut,
    saveBootstrapToken,
    clearBootstrapToken,
    _snapshot: snapshot,
  };
}
